// M13.4 — Ecommerce section on /app/report. Sits AFTER LandingPageTable
// and BEFORE CtaPlacement in the dashboard scroll.
//
// Renders only when `ecommerce?.windowActive === true` — the parent
// gates this. Composition (top to bottom):
//
//   1. Section header — eyebrow + accent bar + title +
//      "figures shown in {currencyCode}" note when known.
//   2. Per-channel revenue stacked area chart (absolute dollars, NOT
//      normalised to 100% — sum of bands at any date = total revenue
//      for that date).
//   3. Per-channel datatable: Channel / Revenue / Transactions / AOV /
//      Revenue per user / Conversion rate, with deltas vs compare.

import type { ReportEcommerce, ReportEcommerceChannelRow } from "../../lib/api";
import { SectionHeader } from "./SectionHeader";
import { ScrollX } from "../ui/scroll-x";
import { InfoTooltip } from "../ui/info-tooltip";
import { CHANNEL_INFO, defaultChannelInfo } from "./classification-info";
import { CurrencyValue } from "./CurrencyValue";
import { EcommerceChart } from "./EcommerceChart";
import { channelColor } from "./channel-colors";
import type { Granularity } from "./granularity";

export interface EcommerceSectionProps {
  ecommerce: ReportEcommerce;
  /** From `meta.currencyCode`. Null = render plain numbers. */
  currencyCode: string | null | undefined;
  granularity: Granularity;
  /** Whether the parent has a compare period active. Drives the
   *  visibility of the Δ columns. */
  compareActive: boolean;
}

export function EcommerceSection({
  ecommerce,
  currencyCode,
  granularity,
  compareActive,
}: EcommerceSectionProps) {
  return (
    <section id="ecommerce" className="scroll-mt-24 space-y-4">
      <div>
        <SectionHeader
          title="Revenue your channels are driving"
          description={
            <>
              Stacked daily revenue by channel. The bands at any date add up to that day&rsquo;s
              total revenue. Below: the per-channel datatable with transactions, average order
              value, revenue per user, and purchaser conversion rate.
            </>
          }
        />
        {currencyCode ? (
          <p className="mt-2 text-xs text-neutral-500">
            Figures shown in <span className="font-semibold text-neutral-700">{currencyCode}</span>{" "}
            (resolved from this property's GA4 settings).
          </p>
        ) : null}
      </div>

      <div className="rounded-2xl bg-white p-5 ring-1 ring-neutral-950/5">
        <p className="mb-3 text-[11px] font-bold uppercase tracking-wide text-neutral-500">
          Revenue trend
        </p>
        <EcommerceChart
          rows={ecommerce.trend}
          granularity={granularity}
          currencyCode={currencyCode}
          ariaLabel="Ecommerce revenue by channel as a stacked area chart"
        />
      </div>

      <ChannelDataTable
        rows={ecommerce.perChannel}
        currencyCode={currencyCode}
        compareActive={compareActive}
      />
    </section>
  );
}

interface ChannelDataTableProps {
  rows: ReportEcommerceChannelRow[];
  currencyCode: string | null | undefined;
  compareActive: boolean;
}

function ChannelDataTable({ rows, currencyCode, compareActive }: ChannelDataTableProps) {
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl bg-white p-5 text-sm text-neutral-500 ring-1 ring-neutral-950/5">
        No per-channel ecommerce data for this window.
      </div>
    );
  }
  return (
    <ScrollX className="rounded-2xl bg-white ring-1 ring-neutral-950/5">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 text-left">
            <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Channel
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Revenue
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Transactions
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              AOV
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Revenue per user
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Conv rate
            </th>
            {compareActive ? (
              <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
                Δ Revenue
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.channel} className="border-b border-neutral-100 last:border-b-0">
              <td className="px-4 py-2.5">
                <span className="inline-flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="inline-block h-2.5 w-2.5 rounded-sm"
                    style={{ backgroundColor: channelColor(r.channel) }}
                  />
                  <span className="font-medium text-neutral-950">{r.channel}</span>
                  <InfoTooltip label={r.channel}>
                    {CHANNEL_INFO[r.channel] ?? defaultChannelInfo}
                  </InfoTooltip>
                </span>
              </td>
              <td className="px-4 py-2.5 text-right text-neutral-950">
                <CurrencyValue value={r.revenue} currencyCode={currencyCode} />
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                {formatNumber(r.purchases)}
              </td>
              <td className="px-4 py-2.5 text-right text-neutral-700">
                <CurrencyValue value={r.averageOrderValue} currencyCode={currencyCode} />
              </td>
              <td className="px-4 py-2.5 text-right text-neutral-700">
                <CurrencyValue value={r.revenuePerUser} currencyCode={currencyCode} />
              </td>
              <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                {(r.purchaserConversionRate * 100).toFixed(2)}%
              </td>
              {compareActive ? (
                <td className="px-4 py-2.5 text-right tabular-nums">
                  {renderDelta(r.deltas.revenue)}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </ScrollX>
  );
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat("en").format(Math.round(n));
}

function renderDelta(d: number | null): React.ReactNode {
  if (d == null || !Number.isFinite(d)) return <span className="text-neutral-400">—</span>;
  const pct = d * 100;
  if (Math.abs(pct) < 0.05) return <span className="text-neutral-500">0.0%</span>;
  const tone = pct > 0 ? "text-emerald-700" : "text-rose-700";
  const arrow = pct > 0 ? "▲" : "▼";
  return (
    <span className={tone}>
      {arrow} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}
